import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, Search, BookOpen } from "lucide-react";

// Jun-2026 — Access Guide: plain-English description of every permission
// module + what each action grants. Data comes from GET /roles/permission-guide
// (same catalog that powers the bulk-upload Excel template).
export function PermissionGuideModal({ onClose }) {
  const [guide, setGuide] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/roles/permission-guide").then(r => setGuide(r.data)).catch(() => setGuide({ sections: [], modules: [] }));
  }, []);
  const filtered = useMemo(() => {
    if (!guide) return [];
    const s = q.trim().toLowerCase();
    if (!s) return guide.modules;
    return guide.modules.filter(m =>
      `${m.key} ${m.label} ${m.section} ${m.description}`.toLowerCase().includes(s));
  }, [guide, q]);
  const bySection = useMemo(() => {
    const map = new Map();
    filtered.forEach(m => {
      if (!map.has(m.section)) map.set(m.section, []);
      map.get(m.section).push(m);
    });
    return [...map.entries()];
  }, [filtered]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="permission-guide-modal">
      <div className="bg-white w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[#F97316]" />
            <div>
              <div className="text-sm font-bold text-slate-900">Permission Access Guide</div>
              <div className="text-[11px] text-slate-500">What each module & action grants — {guide ? guide.modules.length : "…"} modules</div>
            </div>
          </div>
          <button data-testid="guide-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2">
          <Search size={14} className="text-slate-400" />
          <input data-testid="guide-search" value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="Search module, key or description…"
            className="flex-1 text-sm py-1.5 focus:outline-none" />
          {q && <span className="text-[11px] text-slate-400">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>}
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-5">
          {!guide && <div className="text-center text-slate-400 text-sm py-10">Loading…</div>}
          {guide && bySection.length === 0 && <div className="text-center text-slate-400 text-sm py-10">No modules match.</div>}
          {bySection.map(([section, mods]) => (
            <div key={section}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#F97316] mb-2">{section}</div>
              <div className="space-y-2">
                {mods.map(m => (
                  <div key={m.key} className="border border-slate-200 p-3" data-testid={`guide-module-${m.key}`}>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-900">{m.label}</span>
                      <code className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5">{m.key}</code>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{m.description}</p>
                    {m.extra_actions.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mr-1">Granular:</span>
                        {m.extra_actions.map(a => (
                          <span key={a} className="text-[10px] bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5">{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-200 text-[11px] text-slate-500 bg-slate-50">
          Standard actions on every module: View · Create · Edit · Delete · Approve · Export · Upload · Post Payment. "ADD-ON" keys only reveal extra data and never grant module access by themselves. Approve/Reject on vouchers is governed by the Approval Matrix.
        </div>
      </div>
    </div>
  );
}
